/**
 * Lo storico degli aggiornamenti: la coda della pagina Aggiornamenti.
 *
 * Non è una sezione a sé, ed è una scelta di Enrico dell'1 settembre 2026:
 * quella pagina racconta **l'ultimo** tentativo, e questa la storia di tutti
 * gli altri — separarle costringeva a raccontare due volte la stessa cosa in
 * due posti, e a scoprire il secondo per caso.
 *
 * 🔴 **Qui non compare nessun messaggio d'errore.** Il dettaglio di un guasto —
 * l'uscita dei comandi, i percorsi del volume, i comandi di riparazione —
 * resta sul server, fra journal e Telegram, dove arriva solo a chi ha già
 * accesso alla macchina. Essere amministratori dell'applicazione è un'altra
 * cosa. Quello che si vede qui è a quale **passo** l'aggiornamento si è
 * fermato: basta per sapere se chiamare qualcuno, e non disegna la mappa del
 * server. Era la riserva della spec del 10 agosto 2026.
 *
 * ⚠️ **Il pannello «Ultimo tentativo», subito sopra, quella riserva non la
 * rispetta**: mostra `messaggio` e `azione` grezzi in un `<pre>`. È roba di
 * Fase 1, non l'ho toccata perché toglierla è una decisione di Enrico, ma da
 * quando i due stanno sulla stessa pagina l'incoerenza si vede a occhio — la
 * stessa riga compare due volte, sopra col testo intero e sotto senza.
 *
 * La tabella è la stessa della pagina Attività, di proposito: è già la forma
 * con cui in questa applicazione si guarda una cronaca.
 */
import { useEffect, useRef, useState } from 'react';

import { aggiornamenti as api } from '../../api/scadenziario.ts';
import type {
  StoricoAggiornamenti as Storico,
  VoceStoricoAggiornamento,
} from '../../api/tipi-dati.ts';
import { istanteUmano } from '../../componenti/formato.ts';
import { descriviEsito, descriviOrigine, descriviVuoto } from './storico.ts';

interface Props {
  /**
   * L'istante dell'ultimo tentativo, così come lo vede la pagina che ci
   * contiene. Cambia solo quando un aggiornamento finisce, e quando cambia
   * questa tabella si rilegge.
   *
   * 🎯 **Non è un intervallo di tempo, ed è il punto.** La pagina intorno si
   * rilegge da sola ogni minuto; se anche questa lo facesse, chiederebbe
   * sessanta volte all'ora un file che cambia una volta a settimana. Così la
   * rilettura parte **quando c'è qualcosa di nuovo da leggere**, e chi resta a
   * guardare l'aggiornamento che ha appena lanciato vede comparire la riga
   * senza ricaricare niente.
   */
  ultimoTentativoQuando: string | null;
}

export function StoricoAggiornamenti({ ultimoTentativoQuando }: Props) {
  const [storico, setStorico] = useState<Storico | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  /**
   * Se questo caricamento è il primo o una rilettura.
   *
   * È un `ref` e non uno stato per una ragione precisa: deve poter essere
   * letto e scritto **dentro** l'effetto senza diventarne una dipendenza. Con
   * uno stato, l'effetto ripartirebbe a ogni risposta — cioè per sempre.
   */
  const giaCaricato = useRef(false);

  useEffect(() => {
    let annullato = false;

    // Il primo caricamento nasce da una navigazione e passa da `get`: chi
    // arriva qui con la sessione morta dev'essere portato all'accesso. Le
    // riletture partono **da sole** e passano da `getDiFondo`, che una
    // sessione scaduta non la trasforma in un salto al login: chi è rimasto a
    // guardare l'aggiornamento che ha lanciato non deve perdere la pagina.
    const primaVolta = !giaCaricato.current;
    giaCaricato.current = true;

    (primaVolta ? api.storico() : api.storicoDiFondo())
      .then((s) => { if (!annullato) setStorico(s); })
      .catch((e: unknown) => {
        if (!annullato) setErrore(e instanceof Error ? e.message : 'Errore imprevisto');
      });

    return () => { annullato = true; };
  }, [ultimoTentativoQuando]);

  if (errore !== null) {
    return (
      <div className="pannello pannello-imbottito">
        <div className="avviso-errore" role="alert">{errore}</div>
      </div>
    );
  }

  if (storico === null) {
    return (
      <div className="pannello pannello-imbottito">
        <p className="stato-caricamento">Caricamento dello storico…</p>
      </div>
    );
  }

  const vuoto = descriviVuoto(storico.voci.length);

  return (
    <div className="pannello pannello-imbottito">
      <h2 className="sezione-titolo">Storico degli aggiornamenti</h2>
      <p className="aiuto" style={{ maxWidth: '70ch', marginBottom: 16 }}>
        Ogni tentativo, dal più recente. Il dettaglio tecnico di un guasto non compare qui:
        arriva su Telegram e resta nei registri del server.
      </p>

      {vuoto !== null ? (
        <p className="stato-vuoto">{vuoto}</p>
      ) : (
        <div className="tabella-contenitore">
          <table>
            <caption className="solo-lettori-schermo">
              Tentativi di aggiornamento, dal più recente
            </caption>
            <thead>
              <tr>
                <th scope="col">Quando</th>
                <th scope="col">Versione</th>
                <th scope="col">Chi l&apos;ha innescato</th>
                <th scope="col" className="col-espandi">Com&apos;è andata</th>
              </tr>
            </thead>
            <tbody>
              {storico.voci.map((v) => (
                <Riga key={`${v.quando}-${v.versione ?? ''}`} voce={v} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Riga({ voce }: { voce: VoceStoricoAggiornamento }) {
  const esito = descriviEsito(voce);

  return (
    <tr>
      {/* `istanteUmano` e non `istante`: qui le righe sono poche e ognuna è
          un fatto a sé, quindi «ieri alle 02:00» dice in due parole quello che
          una data costringe a calcolare. Nel registro delle attività, dove le
          righe si scorrono a decine, resta `istante`: là una colonna allineata
          si legge più in fretta di frasi tutte diverse. */}
      <td className="data">{istanteUmano(voce.quando)}</td>
      <td>
        {/* Un tentativo può fallire prima di sapere quale versione stesse
            installando: in quel caso il numero non c'è, e va detto invece di
            lasciare una cella vuota che sembra un dato perso. */}
        {voce.versione ?? <span className="tenue">non determinata</span>}
      </td>
      <td className="tenue">{descriviOrigine(voce.origine)}</td>
      <td className="col-espandi">
        <span className={`badge ${esito.badge}`}>{esito.etichetta}</span>
        {esito.dettaglio !== null && <p className="aiuto">{esito.dettaglio}</p>}
      </td>
    </tr>
  );
}
